import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// How long to wait for the section to be drawn: the home page paints its sections as the
// company's data arrives, which on a slow connection is several seconds after the page itself.
const GIVE_UP_MS = 8000;
// Content above the section (images, the fleet) keeps arriving after the scroll and pushes the
// section down; for this long the section is brought back to the top if it has moved.
const SETTLE_MS = 3000;
const DRIFT_PX = 8;

/**
 * Scroll to the section a URL names, once it exists (1NOW-11111).
 *
 * "/#section-faqs" from another page, or opened directly, loads the page before the section is
 * drawn, and the footer's link from the home page ran into the scroll-to-top that follows a
 * navigation. A one-shot scroll at a fixed delay (the old home page and router effects) missed
 * whenever the data was slower than the delay, and then removed the hash from the URL. This
 * waits for the element, scrolls to it smoothly, keeps it at the top while the page above it
 * settles, leaves the hash where it is and gives up after a few seconds. A wheel, touch or
 * key press from the renter ends all of it. ScrollToTop leaves a URL with a hash to this.
 *
 * Designs A and B. Design C resolves its own anchors.
 */
const ScrollToHash: React.FC = () => {
  const { hash, pathname, key } = useLocation();

  useEffect(() => {
    if (hash.length < 2) return;
    let id = hash.slice(1);
    try {
      id = decodeURIComponent(id);
    } catch {
      // a malformed escape is looked up as typed
    }

    const cleanups: Array<() => void> = [];
    const stop = () => {
      cleanups.forEach((fn) => fn());
      cleanups.length = 0;
    };
    const timer = (fn: () => void, ms: number) => {
      const t = setTimeout(fn, ms);
      cleanups.push(() => clearTimeout(t));
    };
    // The renter taking over ends it, so a late section never yanks the page from under them.
    const events = ["wheel", "touchmove", "keydown"] as const;
    events.forEach((name) => {
      window.addEventListener(name, stop, { passive: true });
      cleanups.push(() => window.removeEventListener(name, stop));
    });

    const keepAligned = (element: HTMLElement) => {
      if (typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(() => {
        if (Math.abs(element.getBoundingClientRect().top) > DRIFT_PX) element.scrollIntoView({ block: "start" });
      });
      observer.observe(document.body);
      cleanups.push(() => observer.disconnect());
      timer(stop, SETTLE_MS);
    };
    const scrollTo = (element: HTMLElement) => {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      keepAligned(element);
    };

    const found = document.getElementById(id);
    if (found) {
      scrollTo(found);
    } else {
      const giveUp = setTimeout(stop, GIVE_UP_MS);
      const observer = new MutationObserver(() => {
        const element = document.getElementById(id);
        if (!element) return;
        observer.disconnect();
        clearTimeout(giveUp);
        scrollTo(element);
      });
      observer.observe(document.body, { childList: true, subtree: true });
      cleanups.push(() => observer.disconnect());
      cleanups.push(() => clearTimeout(giveUp));
    }
    return stop;
    // `key` changes on every navigation, so a second click on the same link scrolls again.
  }, [hash, pathname, key]);

  return null;
};

export default ScrollToHash;
